import {_RequestEnvelope} from '../core/types';
import {
    Category,
    CategoryConfig,
    CategoryType,
    CategoryUpdatePatch,
    Company,
    CompanyLiveChatConfig,
    CompanyStripeConfig,
    RealmCategorySpecificConfigSetup,
    RealmCategorySpecificConfigUpdate,
    PaymentMethod,
    RealmBrandingConfig,
    RealmConfig,
    RealmFineraConfig,
    RealmGoogleConfig,
    RealmSummary,
    RealmSupportConfig,
    SiteCacheRule,
    SiteFileDisposition,
    SiteTemplate,
} from '../models/config';
import {ProviderStatus} from '../models/providerstatus';

// ---------------------------------------------------------------------------
// AddCategory
// ---------------------------------------------------------------------------

export interface AddCategoryRequest {
    Id: string;
    Name: string;
    Config: CategoryConfig;
}

export const AddCategoryResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    AlreadyExists: 'AlreadyExists',
    InvalidConfig: 'InvalidConfig',
} as const;
export type AddCategoryResult =
    typeof AddCategoryResultCode[keyof typeof AddCategoryResultCode];

export type AddCategoryResponse =
    | {
    Result: 'Ok' | 'AlreadyExists';
    ResultDescription: string;
    Category: Category;
}
    | {
    Result: 'Unknown' | 'InvalidConfig';
    ResultDescription: string;
    Category: null;
};

const _addCategoryMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'AlreadyExists',
    3: 'InvalidConfig',
};

export function addCategory(
    params: AddCategoryRequest
): _RequestEnvelope<AddCategoryResponse> {
    return {...params, _endpoint: 'admin/config/addCategory', _resultCodeMap: _addCategoryMap};
}

// ---------------------------------------------------------------------------
// AddRealm
// ---------------------------------------------------------------------------

export interface AddRealmRequest {
    Domain: string;
    CompanyId: string;
    Branding: RealmBrandingConfig;
    Support: RealmSupportConfig;
    Google: RealmGoogleConfig;
    Finera: RealmFineraConfig;
    ActivePaymentMethod: PaymentMethod;
    CategoryId: string;
    CategorySpecific: RealmCategorySpecificConfigSetup;
}

export const AddRealmResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    DomainAlreadyUsed: 'DomainAlreadyUsed',
    InvalidDomain: 'InvalidDomain',
    CompanyNotFound: 'CompanyNotFound',
    CompanyDeleted: 'CompanyDeleted',
    InvalidActivePaymentMethod: 'InvalidActivePaymentMethod',
    CategoryNotFound: 'CategoryNotFound',
    CategoryTypeMismatch: 'CategoryTypeMismatch',
} as const;
export type AddRealmResult =
    typeof AddRealmResultCode[keyof typeof AddRealmResultCode];

export type AddRealmResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Config: RealmConfig;
    ProviderStatus: ProviderStatus;
}
    | {
    Result: 'Unknown' | 'DomainAlreadyUsed' | 'InvalidDomain' | 'CompanyNotFound' | 'CompanyDeleted' | 'InvalidActivePaymentMethod' | 'CategoryNotFound' | 'CategoryTypeMismatch';
    ResultDescription: string;
    Config: null;
    ProviderStatus: null;
};

const _addRealmMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'DomainAlreadyUsed',
    3: 'InvalidDomain',
    4: 'CompanyNotFound',
    5: 'CompanyDeleted',
    6: 'InvalidActivePaymentMethod',
    7: 'CategoryNotFound',
    8: 'CategoryTypeMismatch',
};

export function addRealm(
    params: AddRealmRequest
): _RequestEnvelope<AddRealmResponse> {
    return {...params, _endpoint: 'admin/config/addRealm', _resultCodeMap: _addRealmMap};
}

// ---------------------------------------------------------------------------
// AddSiteTemplate
// ---------------------------------------------------------------------------

export interface AddSiteTemplateRequest {
    Id: string;
    CategoryType: CategoryType;
    Name: string;
    Bucket: string;
    BasePrefix: string;
}

export const AddSiteTemplateResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    AlreadyExists: 'AlreadyExists',
} as const;
export type AddSiteTemplateResult =
    typeof AddSiteTemplateResultCode[keyof typeof AddSiteTemplateResultCode];

export type AddSiteTemplateResponse =
    | {
    Result: 'Ok' | 'AlreadyExists';
    ResultDescription: string;
    SiteTemplate: SiteTemplate;
}
    | {
    Result: 'Unknown';
    ResultDescription: string;
    SiteTemplate: null;
};

const _addSiteTemplateMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'AlreadyExists',
};

export function addSiteTemplate(
    params: AddSiteTemplateRequest
): _RequestEnvelope<AddSiteTemplateResponse> {
    return {...params, _endpoint: 'admin/config/addSiteTemplate', _resultCodeMap: _addSiteTemplateMap};
}

// ---------------------------------------------------------------------------
// ChangeRealmCategory
// ---------------------------------------------------------------------------

export interface ChangeRealmCategoryRequest {
    RealmId: string;
    CategoryId: string;
}

export const ChangeRealmCategoryResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    RealmNotFound: 'RealmNotFound',
    CategoryNotFound: 'CategoryNotFound',
    TypeMismatch: 'TypeMismatch',
} as const;
export type ChangeRealmCategoryResult =
    typeof ChangeRealmCategoryResultCode[keyof typeof ChangeRealmCategoryResultCode];

export type ChangeRealmCategoryResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Config: RealmConfig;
}
    | {
    Result: 'Unknown' | 'RealmNotFound' | 'CategoryNotFound' | 'TypeMismatch';
    ResultDescription: string;
    Config: null;
};

const _changeRealmCategoryMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'RealmNotFound',
    3: 'CategoryNotFound',
    4: 'TypeMismatch',
};

export function changeRealmCategory(
    params: ChangeRealmCategoryRequest
): _RequestEnvelope<ChangeRealmCategoryResponse> {
    return {...params, _endpoint: 'admin/config/changeRealmCategory', _resultCodeMap: _changeRealmCategoryMap};
}

// ---------------------------------------------------------------------------
// CreateCompany
// ---------------------------------------------------------------------------

export interface CreateCompanyRequest {
    Name: string;
    Address: string;
    RegistrationDetails: string;
    Country: string;
    Stripe: CompanyStripeConfig;
    LiveChatConfig: CompanyLiveChatConfig;
}

export const CreateCompanyResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NameAlreadyUsed: 'NameAlreadyUsed',
} as const;
export type CreateCompanyResult =
    typeof CreateCompanyResultCode[keyof typeof CreateCompanyResultCode];

export type CreateCompanyResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Company: Company;
}
    | {
    Result: 'Unknown' | 'NameAlreadyUsed';
    ResultDescription: string;
    Company: null;
};

const _createCompanyMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NameAlreadyUsed',
};

export function createCompany(
    params: CreateCompanyRequest
): _RequestEnvelope<CreateCompanyResponse> {
    return {...params, _endpoint: 'admin/config/createCompany', _resultCodeMap: _createCompanyMap};
}

// ---------------------------------------------------------------------------
// DeactivateRealmConfig
// ---------------------------------------------------------------------------

export interface DeactivateRealmConfigRequest {
    RealmId: string;
}

export const DeactivateRealmConfigResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
} as const;
export type DeactivateRealmConfigResult =
    typeof DeactivateRealmConfigResultCode[keyof typeof DeactivateRealmConfigResultCode];

export type DeactivateRealmConfigResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Config: RealmConfig;
    ProviderStatus: ProviderStatus;
}
    | {
    Result: 'Unknown' | 'NotFound';
    ResultDescription: string;
    Config: null;
    ProviderStatus: null;
};

const _deactivateRealmConfigMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
};

export function deactivateRealmConfig(
    params: DeactivateRealmConfigRequest
): _RequestEnvelope<DeactivateRealmConfigResponse> {
    return {...params, _endpoint: 'admin/config/deactivateRealmConfig', _resultCodeMap: _deactivateRealmConfigMap};
}

// ---------------------------------------------------------------------------
// DeleteCompany
// ---------------------------------------------------------------------------

export interface DeleteCompanyRequest {
    CompanyId: string;
}

export const DeleteCompanyResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
    AlreadyDeleted: 'AlreadyDeleted',
    HasActiveRealms: 'HasActiveRealms',
} as const;
export type DeleteCompanyResult =
    typeof DeleteCompanyResultCode[keyof typeof DeleteCompanyResultCode];

export type DeleteCompanyResponse =
    {
        Result: DeleteCompanyResult;
    ResultDescription: string;
};

const _deleteCompanyMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
    3: 'AlreadyDeleted',
    4: 'HasActiveRealms',
};

export function deleteCompany(
    params: DeleteCompanyRequest
): _RequestEnvelope<DeleteCompanyResponse> {
    return {...params, _endpoint: 'admin/config/deleteCompany', _resultCodeMap: _deleteCompanyMap};
}

// ---------------------------------------------------------------------------
// GetCompany
// ---------------------------------------------------------------------------

export interface GetCompanyRequest {
    CompanyId: string;
}

export const GetCompanyResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
} as const;
export type GetCompanyResult =
    typeof GetCompanyResultCode[keyof typeof GetCompanyResultCode];

export type GetCompanyResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Company: Company;
}
    | {
    Result: 'Unknown' | 'NotFound';
    ResultDescription: string;
    Company: null;
};

const _getCompanyMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
};

export function getCompany(
    params: GetCompanyRequest
): _RequestEnvelope<GetCompanyResponse> {
    return {...params, _endpoint: 'admin/config/getCompany', _resultCodeMap: _getCompanyMap};
}

// ---------------------------------------------------------------------------
// GetRealmConfig
// ---------------------------------------------------------------------------

export interface GetRealmConfigRequest {
    RealmId: string;
}

export const GetRealmConfigResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
} as const;
export type GetRealmConfigResult =
    typeof GetRealmConfigResultCode[keyof typeof GetRealmConfigResultCode];

export type GetRealmConfigResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Config: RealmConfig;
    ProviderStatus: ProviderStatus;
}
    | {
    Result: 'Unknown' | 'NotFound';
    ResultDescription: string;
    Config: null;
    ProviderStatus: null;
};

const _getRealmConfigMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
};

export function getRealmConfig(
    params: GetRealmConfigRequest
): _RequestEnvelope<GetRealmConfigResponse> {
    return {...params, _endpoint: 'admin/config/getRealmConfig', _resultCodeMap: _getRealmConfigMap};
}

// ---------------------------------------------------------------------------
// GetSiteTemplate
// ---------------------------------------------------------------------------

export interface GetSiteTemplateRequest {
    SiteTemplateId: string;
}

export const GetSiteTemplateResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
} as const;
export type GetSiteTemplateResult =
    typeof GetSiteTemplateResultCode[keyof typeof GetSiteTemplateResultCode];

export type GetSiteTemplateResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    SiteTemplate: SiteTemplate;
}
    | {
    Result: 'Unknown' | 'NotFound';
    ResultDescription: string;
    SiteTemplate: null;
};

const _getSiteTemplateMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
};

export function getSiteTemplate(
    params: GetSiteTemplateRequest
): _RequestEnvelope<GetSiteTemplateResponse> {
    return {...params, _endpoint: 'admin/config/getSiteTemplate', _resultCodeMap: _getSiteTemplateMap};
}

// ---------------------------------------------------------------------------
// GetSiteTemplateFile
// ---------------------------------------------------------------------------

export interface GetSiteTemplateFileRequest {
    SiteTemplateId: string;
    BucketOverride: string | null;
    BasePrefixOverride: string | null;
    Version: string;
    Path: string;
}

export const GetSiteTemplateFileResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    TemplateNotFound: 'TemplateNotFound',
    FileNotFound: 'FileNotFound',
} as const;
export type GetSiteTemplateFileResult =
    typeof GetSiteTemplateFileResultCode[keyof typeof GetSiteTemplateFileResultCode];

export type GetSiteTemplateFileResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    ContentType: string;
    ContentBase64: string;
    ContentLength: number;
    ETag: string;
    Disposition: SiteFileDisposition;
}
    | {
    Result: 'Unknown' | 'TemplateNotFound' | 'FileNotFound';
    ResultDescription: string;
    ContentType: null;
    ContentBase64: null;
    ContentLength: null;
    ETag: null;
    Disposition: null;
};

const _getSiteTemplateFileMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'TemplateNotFound',
    3: 'FileNotFound',
};

export function getSiteTemplateFile(
    params: GetSiteTemplateFileRequest
): _RequestEnvelope<GetSiteTemplateFileResponse> {
    return {...params, _endpoint: 'admin/config/getSiteTemplateFile', _resultCodeMap: _getSiteTemplateFileMap};
}

// ---------------------------------------------------------------------------
// ListCategories
// ---------------------------------------------------------------------------

export interface ListCategoriesResponse {
    Categories: Category[];
}

export function listCategories(): _RequestEnvelope<ListCategoriesResponse> {
    return {_endpoint: 'admin/config/listCategories', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// ListCompanies
// ---------------------------------------------------------------------------

export interface ListCompaniesRequest {
    ExcludeDeleted: boolean;
}

export interface ListCompaniesResponse {
    Companies: Company[];
}

export function listCompanies(params: ListCompaniesRequest): _RequestEnvelope<ListCompaniesResponse> {
    return {...params, _endpoint: 'admin/config/listCompanies', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// ListRealms
// ---------------------------------------------------------------------------

export interface ListRealmsRequest {
    IncludeInactive: boolean;
}

export interface ListRealmsResponse {
    Realms: RealmSummary[];
}

export function listRealms(params: ListRealmsRequest): _RequestEnvelope<ListRealmsResponse> {
    return {...params, _endpoint: 'admin/config/listRealms', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// ListSiteTemplates
// ---------------------------------------------------------------------------

export interface ListSiteTemplatesResponse {
    SiteTemplates: SiteTemplate[];
}

export function listSiteTemplates(): _RequestEnvelope<ListSiteTemplatesResponse> {
    return {_endpoint: 'admin/config/listSiteTemplates', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// ListSiteTemplateVersions
// ---------------------------------------------------------------------------

export interface ListSiteTemplateVersionsRequest {
    SiteTemplateId: string;
}

export const ListSiteTemplateVersionsResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
} as const;
export type ListSiteTemplateVersionsResult =
    typeof ListSiteTemplateVersionsResultCode[keyof typeof ListSiteTemplateVersionsResultCode];

export type ListSiteTemplateVersionsResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Versions: string[];
    CurrentVersion: string;
}
    | {
    Result: 'Unknown' | 'NotFound';
    ResultDescription: string;
    Versions: null;
    CurrentVersion: null;
};

const _listSiteTemplateVersionsMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
};

export function listSiteTemplateVersions(
    params: ListSiteTemplateVersionsRequest
): _RequestEnvelope<ListSiteTemplateVersionsResponse> {
    return {...params, _endpoint: 'admin/config/listSiteTemplateVersions', _resultCodeMap: _listSiteTemplateVersionsMap};
}

// ---------------------------------------------------------------------------
// ReassignRealmToCompany
// ---------------------------------------------------------------------------

export interface ReassignRealmToCompanyRequest {
    RealmId: string;
    CompanyId: string;
}

export const ReassignRealmToCompanyResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    RealmNotFound: 'RealmNotFound',
    CompanyNotFound: 'CompanyNotFound',
    CompanyDeleted: 'CompanyDeleted',
    InvalidActivePaymentMethod: 'InvalidActivePaymentMethod',
} as const;
export type ReassignRealmToCompanyResult =
    typeof ReassignRealmToCompanyResultCode[keyof typeof ReassignRealmToCompanyResultCode];

export type ReassignRealmToCompanyResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    RealmConfig: RealmConfig;
}
    | {
    Result: 'Unknown' | 'RealmNotFound' | 'CompanyNotFound' | 'CompanyDeleted' | 'InvalidActivePaymentMethod';
    ResultDescription: string;
    RealmConfig: null;
};

const _reassignRealmToCompanyMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'RealmNotFound',
    3: 'CompanyNotFound',
    4: 'CompanyDeleted',
    5: 'InvalidActivePaymentMethod',
};

export function reassignRealmToCompany(
    params: ReassignRealmToCompanyRequest
): _RequestEnvelope<ReassignRealmToCompanyResponse> {
    return {...params, _endpoint: 'admin/config/reassignRealmToCompany', _resultCodeMap: _reassignRealmToCompanyMap};
}

// ---------------------------------------------------------------------------
// SetRealmSiteTemplate
// ---------------------------------------------------------------------------

export interface SetRealmSiteTemplateRequest {
    RealmId: string;
    SiteTemplateId: string | null;
}

export const SetRealmSiteTemplateResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    RealmNotFound: 'RealmNotFound',
    SiteTemplateNotFound: 'SiteTemplateNotFound',
    CategoryTypeMismatch: 'CategoryTypeMismatch',
} as const;
export type SetRealmSiteTemplateResult =
    typeof SetRealmSiteTemplateResultCode[keyof typeof SetRealmSiteTemplateResultCode];

export type SetRealmSiteTemplateResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Config: RealmConfig;
}
    | {
    Result: 'Unknown' | 'RealmNotFound' | 'SiteTemplateNotFound' | 'CategoryTypeMismatch';
    ResultDescription: string;
    Config: null;
};

const _setRealmSiteTemplateMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'RealmNotFound',
    3: 'SiteTemplateNotFound',
    4: 'CategoryTypeMismatch',
};

export function setRealmSiteTemplate(
    params: SetRealmSiteTemplateRequest
): _RequestEnvelope<SetRealmSiteTemplateResponse> {
    return {...params, _endpoint: 'admin/config/setRealmSiteTemplate', _resultCodeMap: _setRealmSiteTemplateMap};
}

// ---------------------------------------------------------------------------
// SetSiteTemplateSource
// ---------------------------------------------------------------------------

export interface SetSiteTemplateSourceRequest {
    SiteTemplateId: string;
    Bucket: string | null;
    BasePrefix: string | null;
    Version: string | null;
}

export const SetSiteTemplateSourceResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
    VersionNotInStorage: 'VersionNotInStorage',
    Conflict: 'Conflict',
} as const;
export type SetSiteTemplateSourceResult =
    typeof SetSiteTemplateSourceResultCode[keyof typeof SetSiteTemplateSourceResultCode];

export type SetSiteTemplateSourceResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    SiteTemplate: SiteTemplate;
}
    | {
    Result: 'Unknown' | 'NotFound' | 'VersionNotInStorage' | 'Conflict';
    ResultDescription: string;
    SiteTemplate: null;
};

const _setSiteTemplateSourceMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
    3: 'VersionNotInStorage',
    4: 'Conflict',
};

export function setSiteTemplateSource(
    params: SetSiteTemplateSourceRequest
): _RequestEnvelope<SetSiteTemplateSourceResponse> {
    return {...params, _endpoint: 'admin/config/setSiteTemplateSource', _resultCodeMap: _setSiteTemplateSourceMap};
}

// ---------------------------------------------------------------------------
// SyncRealmProvider
// ---------------------------------------------------------------------------

export interface SyncRealmProviderRequest {
    RealmId: string;
}

export const SyncRealmProviderResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
} as const;
export type SyncRealmProviderResult =
    typeof SyncRealmProviderResultCode[keyof typeof SyncRealmProviderResultCode];

export type SyncRealmProviderResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    ProviderStatus: ProviderStatus;
}
    | {
    Result: 'Unknown' | 'NotFound';
    ResultDescription: string;
    ProviderStatus: null;
};

const _syncRealmProviderMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
};

export function syncRealmProvider(
    params: SyncRealmProviderRequest
): _RequestEnvelope<SyncRealmProviderResponse> {
    return {...params, _endpoint: 'admin/config/syncRealmProvider', _resultCodeMap: _syncRealmProviderMap};
}

// ---------------------------------------------------------------------------
// UpdateCategory
// ---------------------------------------------------------------------------

export interface UpdateCategoryRequest {
    CategoryId: string;
    Patch: CategoryUpdatePatch;
}

export const UpdateCategoryResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
    TypeMismatch: 'TypeMismatch',
} as const;
export type UpdateCategoryResult =
    typeof UpdateCategoryResultCode[keyof typeof UpdateCategoryResultCode];

export type UpdateCategoryResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Category: Category;
}
    | {
    Result: 'Unknown' | 'NotFound' | 'TypeMismatch';
    ResultDescription: string;
    Category: null;
};

const _updateCategoryMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
    3: 'TypeMismatch',
};

export function updateCategory(
    params: UpdateCategoryRequest
): _RequestEnvelope<UpdateCategoryResponse> {
    return {...params, _endpoint: 'admin/config/updateCategory', _resultCodeMap: _updateCategoryMap};
}

// ---------------------------------------------------------------------------
// UpdateCompany
// ---------------------------------------------------------------------------

export interface UpdateCompanyRequest {
    CompanyId: string;
    Name: string | null;
    Address: string | null;
    RegistrationDetails: string | null;
    CountryCode: string | null;
    Stripe: CompanyStripeConfig | null;
    LiveChatConfig: CompanyLiveChatConfig | null;
}

export const UpdateCompanyResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
    NameAlreadyUsed: 'NameAlreadyUsed',
    InvalidActivePaymentMethod: 'InvalidActivePaymentMethod',
} as const;
export type UpdateCompanyResult =
    typeof UpdateCompanyResultCode[keyof typeof UpdateCompanyResultCode];

export type UpdateCompanyResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Company: Company;
}
    | {
    Result: 'Unknown' | 'NotFound' | 'NameAlreadyUsed' | 'InvalidActivePaymentMethod';
    ResultDescription: string;
    Company: null;
};

const _updateCompanyMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
    3: 'NameAlreadyUsed',
    4: 'InvalidActivePaymentMethod',
};

export function updateCompany(
    params: UpdateCompanyRequest
): _RequestEnvelope<UpdateCompanyResponse> {
    return {...params, _endpoint: 'admin/config/updateCompany', _resultCodeMap: _updateCompanyMap};
}

// ---------------------------------------------------------------------------
// UpdateRealmConfig
// ---------------------------------------------------------------------------

export interface UpdateRealmConfigRequest {
    RealmId: string;
    Branding: RealmBrandingConfig | null;
    Support: RealmSupportConfig | null;
    Google: RealmGoogleConfig | null;
    Finera: RealmFineraConfig | null;
    ActivePaymentMethod: PaymentMethod | null;
    Category: RealmCategorySpecificConfigUpdate | null;
}

export const UpdateRealmConfigResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
    InvalidActivePaymentMethod: 'InvalidActivePaymentMethod',
    TypeMismatch: 'TypeMismatch',
} as const;
export type UpdateRealmConfigResult =
    typeof UpdateRealmConfigResultCode[keyof typeof UpdateRealmConfigResultCode];

export type UpdateRealmConfigResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Config: RealmConfig;
}
    | {
    Result: 'Unknown' | 'NotFound' | 'InvalidActivePaymentMethod' | 'TypeMismatch';
    ResultDescription: string;
    Config: null;
};

const _updateRealmConfigMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
    3: 'InvalidActivePaymentMethod',
    4: 'TypeMismatch',
};

export function updateRealmConfig(
    params: UpdateRealmConfigRequest
): _RequestEnvelope<UpdateRealmConfigResponse> {
    return {...params, _endpoint: 'admin/config/updateRealmConfig', _resultCodeMap: _updateRealmConfigMap};
}

// ---------------------------------------------------------------------------
// UpdateSiteTemplate
// ---------------------------------------------------------------------------

export interface UpdateSiteTemplateRequest {
    SiteTemplateId: string;
    Name: string;
}

export const UpdateSiteTemplateResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
} as const;
export type UpdateSiteTemplateResult =
    typeof UpdateSiteTemplateResultCode[keyof typeof UpdateSiteTemplateResultCode];

export type UpdateSiteTemplateResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    SiteTemplate: SiteTemplate;
}
    | {
    Result: 'Unknown' | 'NotFound';
    ResultDescription: string;
    SiteTemplate: null;
};

const _updateSiteTemplateMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
};

export function updateSiteTemplate(
    params: UpdateSiteTemplateRequest
): _RequestEnvelope<UpdateSiteTemplateResponse> {
    return {...params, _endpoint: 'admin/config/updateSiteTemplate', _resultCodeMap: _updateSiteTemplateMap};
}

// ---------------------------------------------------------------------------
// ValidateSiteTemplateVersion
// ---------------------------------------------------------------------------

export interface ValidateSiteTemplateVersionRequest {
    SiteTemplateId: string;
    BucketOverride: string | null;
    BasePrefixOverride: string | null;
    Version: string;
}

export const ValidateSiteTemplateVersionResultCode = {
    Unknown: 'Unknown',
    OkWithManifest: 'OkWithManifest',
    OkNoManifest: 'OkNoManifest',
    NotFound: 'NotFound',
    VersionNotInStorage: 'VersionNotInStorage',
} as const;
export type ValidateSiteTemplateVersionResult =
    typeof ValidateSiteTemplateVersionResultCode[keyof typeof ValidateSiteTemplateVersionResultCode];

export type ValidateSiteTemplateVersionResponse =
    | {
    Result: 'OkWithManifest';
    ResultDescription: string;
    Rules: SiteCacheRule[];
    ManifestErrors: string[];
}
    | {
    Result: 'Unknown' | 'OkNoManifest' | 'NotFound' | 'VersionNotInStorage';
    ResultDescription: string;
    Rules: null;
    ManifestErrors: null;
};

const _validateSiteTemplateVersionMap: Record<number, string> = {
    0: 'Unknown',
    1: 'OkWithManifest',
    2: 'OkNoManifest',
    3: 'NotFound',
    4: 'VersionNotInStorage',
};

export function validateSiteTemplateVersion(
    params: ValidateSiteTemplateVersionRequest
): _RequestEnvelope<ValidateSiteTemplateVersionResponse> {
    return {...params, _endpoint: 'admin/config/validateSiteTemplateVersion', _resultCodeMap: _validateSiteTemplateVersionMap};
}
